import React, { ReactNode } from "react"
import RadioButtons from "components/ReactHookForm/RadioButtons"
import { Control } from "react-hook-form"
import { GuestStrategy as GuestStrategyType } from "sharedTypes/index"
import { Box, Text } from "@chakra-ui/react"
import UpgradeAlert from "components/elements/UpgradeAlert"
import useCurrentPlanType from "utilities/useCurrentPlanType"

type Props = {
  control: Control
}

export const getGuestStrategyNameFromType = (type: GuestStrategyType) => {
  const selectedStrategy = [
    { key: GuestStrategyType.Create, value: "Always create a new guest" },
    { key: GuestStrategyType.Upsert, value: "Prevent creating duplicate guests" },
    { key: GuestStrategyType.Restrict, value: "Restrict access to existing guests" },
  ].find((option) => (option.key === type))

  if (!selectedStrategy) {
    return ""
  }

  return selectedStrategy.value
}

const GuestStrategy = ({ control }: Props) => {
  const { corporateFeaturesEnabled } = useCurrentPlanType()

  const guestCreationOptions = [
    {
      label:
      getGuestStrategyNameFromType(GuestStrategyType.Create),
      value: GuestStrategyType.Create,
      additionalElement: (
        <GuestCreationOptionAdditionalElement>
          A new guest will be created for every form submission,
          even if the email matches an existing guest.
        </GuestCreationOptionAdditionalElement>
      ),
    },
    {
      label:
       getGuestStrategyNameFromType(GuestStrategyType.Upsert),
      value: GuestStrategyType.Upsert,
      additionalElement: (
        <GuestCreationOptionAdditionalElement>
          If a guest with the same email already exists in this
          event, the registration will be saved under their existing profile.
        </GuestCreationOptionAdditionalElement>
      ),
    },
    {
      label:
      getGuestStrategyNameFromType(GuestStrategyType.Restrict),
      value: GuestStrategyType.Restrict,
      additionalElement: (
        <GuestCreationOptionAdditionalElement showUpgradeAlert={!corporateFeaturesEnabled}>
          Only guests already added to this event can register.
          If the email entered doesn’t match an existing guest,
          the form submission will be blocked.
        </GuestCreationOptionAdditionalElement>
      ),
      disabled: !corporateFeaturesEnabled,
    },
  ]

  return (
    <RadioButtons
      label="Guest Creation"
      name="guestStrategy"
      control={control}
      options={guestCreationOptions}
      spacing={5}
    />
  )
}

export default GuestStrategy

type GuestCreationOptionAdditionalElementProps = {
  children: ReactNode
  showUpgradeAlert?: boolean
}

const GuestCreationOptionAdditionalElement = (
  { children, showUpgradeAlert }: GuestCreationOptionAdditionalElementProps,
) => (
  <Box pl={7} mt={-4}>
    <Text fontSize="sm">
      {children}
    </Text>
    {showUpgradeAlert && (
      <UpgradeAlert noBorder text="Requires Corporate Subscription" />
    )}
  </Box>
)
